import QRCode from 'qrcode';
import { isNative, shareImageNative } from './native.js';

/**
 * Paylaşılabilir kod görseli üretir: üstte QR, altında iki kod yazılı.
 *
 * WhatsApp'a gönderilmek üzere tasarlandı — karşı taraf hem QR'ı okutabilsin
 * hem kodları gözle görebilsin. QR her zaman barkodu taşır; ürün kodu
 * yalnızca yazı olarak görünür.
 */

export interface KodGorseliGirdi {
  /** QR'a gömülecek kod — her zaman barkod. */
  qrKodu: string;
  satirlar: Array<{ etiket: string; deger: string }>;
  /** Görselin altına küçük not (kategori adı gibi). */
  altNot?: string;
}

const GENISLIK = 720;
const KENAR = 48;
const QR_BOYUT = 460;

export async function kodGorseliUret(girdi: KodGorseliGirdi): Promise<Blob> {
  const qrDataUrl = await QRCode.toDataURL(girdi.qrKodu, {
    width: QR_BOYUT,
    margin: 1,
    errorCorrectionLevel: 'M',
    color: { dark: '#1c2530', light: '#ffffff' },
  });

  const qrResim = await resimYukle(qrDataUrl);

  // Yükseklik satır sayısına göre değişiyor; sabit tutulsa kodlar sığmazdı.
  const satirYuksekligi = 92;
  const yukseklik =
    KENAR + QR_BOYUT + 36 + girdi.satirlar.length * satirYuksekligi + KENAR +
    (girdi.altNot ? 34 : 0);

  const tuval = document.createElement('canvas');
  tuval.width = GENISLIK;
  tuval.height = yukseklik;

  const ctx = tuval.getContext('2d');
  if (!ctx) throw new Error('Görsel oluşturulamadı');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, GENISLIK, yukseklik);

  ctx.drawImage(qrResim, (GENISLIK - QR_BOYUT) / 2, KENAR, QR_BOYUT, QR_BOYUT);

  let y = KENAR + QR_BOYUT + 36;
  ctx.textAlign = 'center';

  for (const satir of girdi.satirlar) {
    ctx.fillStyle = '#6b7785';
    ctx.font = '500 24px -apple-system, "Segoe UI", Roboto, sans-serif';
    ctx.fillText(satir.etiket.toUpperCase(), GENISLIK / 2, y);

    ctx.fillStyle = '#1c2530';
    ctx.font = 'bold 42px -apple-system, "Segoe UI", Roboto, sans-serif';
    // Uzun kodlar kenara taşmasın diye gerekirse sıkıştırılır.
    const enFazla = GENISLIK - KENAR * 2;
    ctx.fillText(satir.deger, GENISLIK / 2, y + 46, enFazla);

    y += satirYuksekligi;
  }

  if (girdi.altNot) {
    ctx.fillStyle = '#9aa4b0';
    ctx.font = '400 22px -apple-system, "Segoe UI", Roboto, sans-serif';
    ctx.fillText(girdi.altNot, GENISLIK / 2, y + 4);
  }

  return new Promise((resolve, reject) => {
    tuval.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Görsel oluşturulamadı'))),
      'image/png',
    );
  });
}

function resimYukle(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('QR görseli yüklenemedi'));
    img.src = src;
  });
}

/**
 * Görseli paylaşır.
 *
 * Üç ayrı yol var çünkü ortamlar farklı davranıyor:
 *  - APK: `navigator.share` ve `<a download>` WebView'de ÇALIŞMAZ (ikisi de
 *    tarayıcı özelliği). Paylaşım native köprüden yapılır.
 *  - Mobil tarayıcı: Web Share API dosya paylaşımını destekler.
 *  - Masaüstü: paylaşım yok, dosya indirilir.
 */
export async function gorseliPaylas(
  blob: Blob,
  dosyaAdi: string,
  /** Paylaşım penceresine giden açıklama — WhatsApp'ta resmin altyazısı olur. */
  aciklama?: string,
): Promise<'paylasildi' | 'indirildi'> {
  if (isNative()) {
    await shareImageNative(await blobToDataUri(blob), dosyaAdi, aciklama);
    return 'paylasildi';
  }

  const dosya = new File([blob], dosyaAdi, { type: 'image/png' });

  if (navigator.canShare?.({ files: [dosya] })) {
    try {
      await navigator.share({ files: [dosya], text: aciklama });
      return 'paylasildi';
    } catch (err) {
      // Kullanıcı pencereyi kapattıysa indirmeye düşmek yanlış olur.
      if (err instanceof DOMException && err.name === 'AbortError') {
        return 'paylasildi';
      }
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = dosyaAdi;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);

  return 'indirildi';
}

function blobToDataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const okuyucu = new FileReader();
    okuyucu.onload = () => resolve(String(okuyucu.result));
    okuyucu.onerror = () => reject(new Error('Görsel okunamadı'));
    okuyucu.readAsDataURL(blob);
  });
}
